import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lightning, CheckCircle } from "@phosphor-icons/react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Explainer } from "../components/friendly";

export default function Billing() {
  const { refreshOrg } = useApp();
  const [packages, setPackages] = useState({});
  const [credits, setCredits] = useState(null);
  const [params, setParams] = useSearchParams();
  const [payState, setPayState] = useState("");

  const load = useCallback(() => {
    api.get("/billing/packages").then((r) => setPackages(r.data));
    api.get("/credits").then((r) => setCredits(r.data));
  }, []);
  useEffect(load, [load]);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    let attempts = 0;
    setPayState("checking");
    const poll = async () => {
      if (attempts++ >= 6) { setPayState("timeout"); return; }
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPayState("paid");
          toast.success(`Payment successful — ${data.credits} credits added!`);
          setParams({});
          refreshOrg(); load();
          return;
        }
        if (data.status === "expired") { setPayState("expired"); return; }
        setTimeout(poll, 2000);
      } catch { setTimeout(poll, 2000); }
    };
    poll();
  }, [params, setParams, refreshOrg, load]);

  const buy = async (packageId) => {
    try {
      const { data } = await api.post("/billing/checkout", { package_id: packageId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
    }
  };

  return (
    <div className="space-y-6 fade-up max-w-4xl" data-testid="billing-page">
      <div>
        <h1 className="font-display font-bold text-3xl tracking-tight">Billing & Credits</h1>
        <p className="text-sm text-slate-500 mt-1">Text ≈1 · Image ≈3 · Video ≈15 · SEO article ≈10 credits.</p>
      </div>
      <Explainer id="billing">Credits are the app's fuel: 1 for a text post, 3 for an image, 10 for an SEO article, 15 for a video. If a generation ever fails, the credits come straight back automatically.</Explainer>

      {payState === "checking" && <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700" data-testid="payment-checking">Verifying your payment…</div>}
      {payState === "paid" && <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-700 flex items-center gap-2" data-testid="payment-success"><CheckCircle size={16} weight="fill" /> Payment confirmed — credits added.</div>}
      {(payState === "expired" || payState === "timeout") && <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-600" data-testid="payment-failed">We couldn't confirm the payment. If you were charged, credits will appear shortly.</div>}

      <div className="bg-slate-950 text-white rounded-2xl p-8 card-float flex items-center justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-slate-400 font-medium">Current balance</p>
          <p className="font-display font-bold text-5xl mt-2 flex items-center gap-3" data-testid="billing-balance"><Lightning size={30} weight="fill" className="text-[hsl(9,69%,68%)]" />{credits?.balance ?? "—"}</p>
        </div>
        <p className="text-[12px] text-slate-400 max-w-[180px] text-right hidden sm:block">Credits are shared across your whole agency workspace.</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        {Object.entries(packages).map(([id, p]) => (
          <div key={id} className={`bg-white border rounded-2xl p-6 card-float hover-lift ${id === "growth" ? "border-[hsl(9,69%,62%)]" : "border-slate-200/80"}`}>
            {id === "growth" && <span className="text-[10px] uppercase tracking-widest font-bold text-[hsl(9,55%,48%)]">Most popular</span>}
            <h3 className="font-display font-bold text-lg mt-1">{p.label}</h3>
            <p className="font-display font-bold text-3xl mt-2">${p.price}</p>
            <p className="text-[13px] text-slate-500 mt-1">{p.credits} credits</p>
            <Button data-testid={`buy-package-${id}`} onClick={() => buy(id)} className={`w-full mt-5 rounded-lg ${id === "growth" ? "bg-[hsl(9,69%,62%)] hover:bg-[hsl(8,54%,54%)]" : "bg-slate-900 hover:bg-slate-700"}`}>Buy {p.label}</Button>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200/80 rounded-xl card-float">
        <div className="px-5 py-4 border-b border-slate-100"><h2 className="font-display font-bold text-base">Usage history</h2></div>
        <div className="divide-y divide-slate-100">
          {(credits?.transactions || []).map((tx) => (
            <div key={tx.tx_id} className="px-5 py-2.5 flex items-center justify-between text-[13px]">
              <span className="text-slate-600">{tx.note || tx.kind}</span>
              <span className={`font-semibold ${tx.amount > 0 ? "text-emerald-600" : "text-slate-500"}`}>{tx.amount > 0 ? "+" : ""}{tx.amount}</span>
            </div>
          ))}
          {!credits?.transactions?.length && <p className="px-5 py-8 text-sm text-slate-400">No usage yet.</p>}
        </div>
      </div>
    </div>
  );
}
